/**
 * Domain Pack Detail Page
 * Comprehensive view of domain pack with tabbed interface for all research components
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Brain,
  Database,
  BarChart3,
  Edit2,
  Archive,
  CheckCircle,
  Clock,
  FileText,
  Plus,
  Sparkles,
  History,
  ChevronDown,
  ChevronRight,
  Link,
  ArrowRight,
  RefreshCw,
  Network,
  Share2,
  TrendingUp,
  AlertTriangle,
  Award,
  Lightbulb,
} from 'lucide-react';

import { getDomainPack, getDomainPackStatistics, getDomainPackReviews, getDomainPackQuality, getSubdomainRelationships, triggerRelationshipDiscovery, type ResearchRelationship } from '../lib/api/domainPacksApi';
import { STATUS_COLORS, STATUS_NAMES } from '../types/domainPack';
import type { Subdomain, DomainPackDetail as DomainPackDetailType, DomainPackStatistics as StatsType } from '../types/domainPack';
import { AddSubdomainModal } from '../components/domain-packs/AddSubdomainModal';
import { EditSubdomainModal } from '../components/domain-packs/EditSubdomainModal';
import { SubdomainSuggestionsModal } from '../components/domain-packs/SubdomainSuggestionsModal';
import QualityDashboard from '../components/domain-packs/QualityDashboard';
import ReviewHistory from '../components/domain-packs/ReviewHistory';
import { VersionHistoryTab } from '../components/domain-packs/VersionHistoryTab';
import { IssueResolutionCanvas } from '../components/issue-resolution-canvas';
import type { Issue } from '../types/domainPack';

type Tab = 'overview' | 'subdomains' | 'relationships' | 'data' | 'quality' | 'reviews' | 'statistics' | 'versions';

const DomainPackDetail: React.FC = () => {
  const navigate = useNavigate();
  const { domainPackId } = useParams<{ domainPackId: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showAddSubdomainModal, setShowAddSubdomainModal] = useState(false);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [editingSubdomain, setEditingSubdomain] = useState<Subdomain | null>(null);
  // Issue Resolution Canvas state
  const [canvasIssue, setCanvasIssue] = useState<{ issue: Issue; subdomain: Subdomain } | null>(null);

  // Fetch domain pack details
  const {
    data: domainPack,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['domain-pack', domainPackId],
    queryFn: () => getDomainPack(domainPackId!),
    enabled: !!domainPackId,
  });

  // Fetch statistics
  const { data: statistics } = useQuery({
    queryKey: ['domain-pack-statistics', domainPackId],
    queryFn: () => getDomainPackStatistics(domainPackId!),
    enabled: !!domainPackId && activeTab === 'statistics',
  });

  // Fetch reviews for count badge
  const { data: reviewsData } = useQuery({
    queryKey: ['domain-pack-reviews', domainPackId],
    queryFn: () => getDomainPackReviews(domainPackId!),
    enabled: !!domainPackId,
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Fetch quality assessment data for header score
  const { data: qualityData } = useQuery({
    queryKey: ['domain-pack-quality', domainPackId],
    queryFn: () => getDomainPackQuality(domainPackId!),
    enabled: !!domainPackId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !domainPack) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">
            Domain Pack Not Found
          </h2>
          <p className="text-neutral-600 mb-6">
            The requested domain pack could not be found or you don't have access to it.
          </p>
          <button
            onClick={() => navigate('/domain-packs')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  // Use quality assessment score if available, otherwise fall back to domain pack quality_score
  const displayQualityScore = qualityData?.assessed && qualityData?.overall_score != null
    ? qualityData.overall_score / 100  // Convert from 0-100 to 0-1 scale
    : domainPack.quality_score;

  const qualityColor =
    displayQualityScore >= 0.85
      ? 'text-green-600'
      : displayQualityScore >= 0.7
      ? 'text-yellow-600'
      : 'text-red-600';

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header — constrained to the same max-width as the body so the top and
          bottom sections align at the same width. */}
      <div className="max-w-screen-2xl mx-auto px-6 pt-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <button
            onClick={() => navigate('/domain-packs')}
            className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Library
          </button>

          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Brain className="w-8 h-8 text-primary-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-neutral-900">
                    {domainPack.domain_name}
                  </h1>
                  <span
                    className={`inline-block px-3 py-1 text-sm font-medium rounded ${
                      STATUS_COLORS[domainPack.status]
                    }`}
                  >
                    {STATUS_NAMES[domainPack.status]}
                  </span>
                </div>
                <p className="text-neutral-600 mb-3">{domainPack.description}</p>
                {domainPack.industry_context && (
                  <p className="text-sm text-neutral-500">
                    Industry: {domainPack.industry_context}
                  </p>
                )}
              </div>
            </div>

            {/* Quality Score */}
            <div className="text-right ml-6">
              <div className={`text-4xl font-bold ${qualityColor}`}>
                {(displayQualityScore * 100).toFixed(0)}%
              </div>
              <div className="text-sm text-neutral-500">
                {qualityData?.assessed ? 'AI Assessment Score' : 'Quality Score'}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-neutral-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-neutral-900">{domainPack.subdomains.length}</div>
              <div className="text-sm text-neutral-600">Subdomains</div>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-neutral-900">{domainPack.hypothesis_count}</div>
              <div className="text-sm text-neutral-600">Hypotheses</div>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-neutral-900">{domainPack.case_application_count}</div>
              <div className="text-sm text-neutral-600">Case Applications</div>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-neutral-900">
                {new Date(domainPack.updated_at).toLocaleDateString()}
              </div>
              <div className="text-sm text-neutral-600">Last Updated</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-1 overflow-x-auto">
            <TabButton
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
              icon={FileText}
              label="Overview"
            />
            <TabButton
              active={activeTab === 'subdomains'}
              onClick={() => setActiveTab('subdomains')}
              icon={Brain}
              label={`Subdomains (${domainPack.subdomains.length})`}
            />
            <TabButton
              active={activeTab === 'relationships'}
              onClick={() => setActiveTab('relationships')}
              icon={Share2}
              label="Relationships"
            />
            <TabButton
              active={activeTab === 'data'}
              onClick={() => setActiveTab('data')}
              icon={Database}
              label={`Data (${domainPack.data_element_count})`}
            />
            <TabButton
              active={activeTab === 'quality'}
              onClick={() => setActiveTab('quality')}
              icon={CheckCircle}
              label="Quality"
            />
            <TabButton
              active={activeTab === 'reviews'}
              onClick={() => setActiveTab('reviews')}
              icon={Clock}
              label={`Reviews (${reviewsData?.total_reviews || 0})`}
            />
            <TabButton
              active={activeTab === 'statistics'}
              onClick={() => setActiveTab('statistics')}
              icon={BarChart3}
              label="Statistics"
            />
            <TabButton
              active={activeTab === 'versions'}
              onClick={() => setActiveTab('versions')}
              icon={History}
              label="Versions"
            />
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        {activeTab === 'overview' && <OverviewTab domainPack={domainPack} />}
        {activeTab === 'subdomains' && (
          <SubdomainsTab
            subdomains={domainPack.subdomains}
            domainPackId={domainPackId!}
            onAddClick={() => setShowAddSubdomainModal(true)}
            onSuggestClick={() => setShowSuggestionsModal(true)}
            onEditClick={(subdomain) => setEditingSubdomain(subdomain)}
            onResearchClick={(subdomain) => {
              // Navigate to progress page with research trigger
              navigate(
                `/domain-packs/${domainPackId}/progress?subdomain_id=${subdomain.subdomain_id}&trigger_research=true`
              );
            }}
            onOpenIssueCanvas={(issue, subdomain) => setCanvasIssue({ issue, subdomain })}
          />
        )}
        {activeTab === 'relationships' && (
          <RelationshipsTab
            subdomains={domainPack.subdomains}
            domainPackId={domainPackId!}
          />
        )}
        {activeTab === 'data' && <DataElementsTab subdomains={domainPack.subdomains} />}
        {activeTab === 'quality' && (
          <QualityDashboard
            domainPackId={domainPackId!}
            domainName={domainPack.domain_name}
          />
        )}
        {activeTab === 'reviews' && (
          <ReviewHistory domainPackId={domainPackId!} />
        )}
        {activeTab === 'statistics' && (
          <StatisticsTab domainPack={domainPack} statistics={statistics} />
        )}
        {activeTab === 'versions' && (
          <VersionHistoryTab domainPackId={domainPackId!} />
        )}
      </div>

      {/* Add Subdomain Modal */}
      <AddSubdomainModal
        domainPackId={domainPackId!}
        domainPackName={domainPack.domain_name}
        isOpen={showAddSubdomainModal}
        onClose={() => setShowAddSubdomainModal(false)}
      />

      {/* Edit Subdomain Modal */}
      {editingSubdomain && (
        <EditSubdomainModal
          domainPackId={domainPackId!}
          subdomain={{
            subdomain_id: editingSubdomain.subdomain_id,
            subdomain_name: editingSubdomain.name,
            description: editingSubdomain.description,
            scope: editingSubdomain.scope,
          }}
          isOpen={!!editingSubdomain}
          onClose={() => setEditingSubdomain(null)}
        />
      )}

      {/* Subdomain Suggestions Modal */}
      <SubdomainSuggestionsModal
        domainPackId={domainPackId!}
        domainPackName={domainPack.domain_name}
        isOpen={showSuggestionsModal}
        onClose={() => setShowSuggestionsModal(false)}
      />

      {/* Issue Resolution Canvas - Full Page Overlay */}
      {canvasIssue && (
        <div className="fixed inset-0 z-50 bg-white overflow-auto">
          <IssueResolutionCanvas
            issue={canvasIssue.issue}
            subdomain={canvasIssue.subdomain}
            domainPackId={domainPackId!}
            onClose={() => setCanvasIssue(null)}
            onViewInGraph={() => {
              // Switch to relationships tab and focus on this issue
              setCanvasIssue(null);
              setActiveTab('relationships');
            }}
          />
        </div>
      )}
    </div>
  );
};

// Tab Button Component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<any>;
  label: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
      active
        ? 'bg-primary-600 text-white'
        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

// Overview Tab
const OverviewTab: React.FC<{ domainPack: DomainPackDetailType }> = ({ domainPack }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg border border-neutral-200 p-6">
      <h2 className="text-xl font-semibold text-neutral-900 mb-4">Domain Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Domain Pack ID</label>
          <p className="text-neutral-900 font-mono text-sm">{domainPack.domain_pack_id}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
          <p className="text-neutral-900">{STATUS_NAMES[domainPack.status]}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Created At</label>
          <p className="text-neutral-900">{new Date(domainPack.created_at).toLocaleString()}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Updated At</label>
          <p className="text-neutral-900">{new Date(domainPack.updated_at).toLocaleString()}</p>
        </div>
        {domainPack.llm_provider && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">LLM Provider</label>
            <p className="text-neutral-900">{domainPack.llm_provider} / {domainPack.llm_model}</p>
          </div>
        )}
        {domainPack.total_tokens && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Total Tokens</label>
            <p className="text-neutral-900">{domainPack.total_tokens.toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>

    <div className="bg-white rounded-lg border border-neutral-200 p-6">
      <h2 className="text-xl font-semibold text-neutral-900 mb-4">Research Summary</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={Brain} label="Subdomains" value={domainPack.subdomains.length} />
        <StatCard icon={TrendingUp} label="Trends" value={domainPack.trend_count} />
        <StatCard icon={AlertTriangle} label="Issues" value={domainPack.issue_count} />
        <StatCard icon={Award} label="Best Practices" value={domainPack.practice_count} />
        <StatCard icon={Database} label="Data Elements" value={domainPack.data_element_count} />
        <StatCard icon={Lightbulb} label="Hypotheses" value={domainPack.hypothesis_count} />
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{ icon: React.ComponentType<any>; label: string; value: number }> = ({
  icon: Icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
    <Icon className="w-8 h-8 text-primary-600" />
    <div>
      <div className="text-2xl font-bold text-neutral-900">{value}</div>
      <div className="text-sm text-neutral-600">{label}</div>
    </div>
  </div>
);

// Subdomains Tab - Enhanced with expandable sections for each subdomain
const SubdomainsTab: React.FC<{
  subdomains: Subdomain[];
  domainPackId: string;
  onAddClick: () => void;
  onSuggestClick: () => void;
  onEditClick: (subdomain: Subdomain) => void;
  onResearchClick: (subdomain: Subdomain) => void;
  onOpenIssueCanvas?: (issue: Issue, subdomain: Subdomain) => void;
}> = ({ subdomains, domainPackId, onAddClick, onSuggestClick, onEditClick, onResearchClick, onOpenIssueCanvas }) => {
  const [expandedSubdomains, setExpandedSubdomains] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [subdomainRelationships, setSubdomainRelationships] = useState<Record<string, ResearchRelationship[]>>({});
  const [loadingRelationships, setLoadingRelationships] = useState<Set<string>>(new Set());
  const [discoveringRelationships, setDiscoveringRelationships] = useState<Set<string>>(new Set());

  const toggleSubdomain = (subdomainId: string) => {
    setExpandedSubdomains((prev) => {
      const next = new Set(prev);
      if (next.has(subdomainId)) {
        next.delete(subdomainId);
      } else {
        next.add(subdomainId);
        // Fetch relationships when expanding
        fetchRelationships(subdomainId);
      }
      return next;
    });
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  // Fetch relationships when subdomain is expanded
  const fetchRelationships = async (subdomainId: string) => {
    if (subdomainRelationships[subdomainId] || loadingRelationships.has(subdomainId)) {
      return; // Already loaded or loading
    }

    setLoadingRelationships((prev) => new Set(prev).add(subdomainId));

    try {
      const response = await getSubdomainRelationships(domainPackId, subdomainId);
      setSubdomainRelationships((prev) => ({
        ...prev,
        [subdomainId]: response.relationships || [],
      }));
    } catch (error) {
      console.error('Failed to fetch relationships:', error);
      setSubdomainRelationships((prev) => ({
        ...prev,
        [subdomainId]: [],
      }));
    } finally {
      setLoadingRelationships((prev) => {
        const next = new Set(prev);
        next.delete(subdomainId);
        return next;
      });
    }
  };

  // Trigger relationship discovery
  const handleDiscoverRelationships = async (subdomainId: string) => {
    setDiscoveringRelationships((prev) => new Set(prev).add(subdomainId));

    try {
      await triggerRelationshipDiscovery(domainPackId, subdomainId, true);
      // Wait a bit for discovery to complete, then refresh
      setTimeout(async () => {
        // Clear cached relationships and refetch
        setSubdomainRelationships((prev) => {
          const next = { ...prev };
          delete next[subdomainId];
          return next;
        });
        await fetchRelationships(subdomainId);
        setDiscoveringRelationships((prev) => {
          const next = new Set(prev);
          next.delete(subdomainId);
          return next;
        });
      }, 5000);
    } catch (error) {
      console.error('Failed to discover relationships:', error);
      setDiscoveringRelationships((prev) => {
        const next = new Set(prev);
        next.delete(subdomainId);
        return next;
      });
    }
  };

  // Helper to get relationships for a specific item
  const getRelationshipsForItem = (subdomainId: string, itemId: string): ResearchRelationship[] => {
    const rels = subdomainRelationships[subdomainId] || [];
    return rels.filter((r) => r.sourceId === itemId || r.targetId === itemId);
  };

  // Helper to get relationship type display info
  const getRelationshipTypeInfo = (type: string) => {
    const types: Record<string, { label: string; color: string; icon: string }> = {
      DRIVES: { label: 'Drives', color: 'bg-red-100 text-red-700', icon: '→' },
      ADDRESSED_BY: { label: 'Addressed by', color: 'bg-green-100 text-green-700', icon: '✓' },
      MITIGATES: { label: 'Mitigates', color: 'bg-green-100 text-green-700', icon: '↓' },
      INVESTIGATES: { label: 'Investigates', color: 'bg-purple-100 text-purple-700', icon: '?' },
      REQUIRES: { label: 'Requires', color: 'bg-blue-100 text-blue-700', icon: '⬢' },
      VALIDATES: { label: 'Validates', color: 'bg-teal-100 text-teal-700', icon: '✔' },
      ENABLES: { label: 'Enables', color: 'bg-indigo-100 text-indigo-700', icon: '⚡' },
      ACCELERATES: { label: 'Accelerates', color: 'bg-orange-100 text-orange-700', icon: '⏩' },
    };
    return types[type] || { label: type, color: 'bg-neutral-100 text-neutral-700', icon: '•' };
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-blue-100 text-blue-700',
    };
    return colors[severity] || colors.medium;
  };

  return (
    <div className="space-y-4">
      {/* Header with Add and Suggest buttons */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-neutral-900">Subdomains ({subdomains.length})</h2>
          <p className="text-sm text-neutral-600 mt-1">Click to expand and view all research for each subdomain</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onSuggestClick}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Get AI Suggestions
          </button>
          <button
            onClick={onAddClick}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Subdomain
          </button>
        </div>
      </div>

      {subdomains.map((subdomain) => {
        const isExpanded = expandedSubdomains.has(subdomain.subdomain_id);
        const totalItems = subdomain.trends.length + subdomain.issues.length +
          subdomain.practices.length + subdomain.data_elements.length + subdomain.hypotheses.length;

        return (
          <div
            key={subdomain.subdomain_id}
            className="bg-white rounded-lg border border-neutral-200 overflow-hidden"
          >
            {/* Subdomain Header - Clickable to expand */}
            <div
              onClick={() => toggleSubdomain(subdomain.subdomain_id)}
              className="p-6 cursor-pointer hover:bg-neutral-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-neutral-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-neutral-500" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1">{subdomain.name}</h3>
                    <p className="text-neutral-600 text-sm">{subdomain.description}</p>
                    {subdomain.scope && (
                      <p className="text-sm text-neutral-500 mt-2">
                        <strong>Scope:</strong> {subdomain.scope}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex items-center gap-6">
                  <div>
                    <div className="text-sm text-neutral-500">{totalItems} items</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600">
                      {(subdomain.quality_score * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-neutral-500">Quality</div>
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-5 gap-3 text-sm mt-4" onClick={(e) => e.stopPropagation()}>
                <div className="text-center p-2 bg-blue-50 rounded">
                  <div className="font-semibold text-blue-900">{subdomain.trends.length}</div>
                  <div className="text-blue-600 text-xs">Trends</div>
                </div>
                <div className="text-center p-2 bg-orange-50 rounded">
                  <div className="font-semibold text-orange-900">{subdomain.issues.length}</div>
                  <div className="text-orange-600 text-xs">Issues</div>
                </div>
                <div className="text-center p-2 bg-purple-50 rounded">
                  <div className="font-semibold text-purple-900">{subdomain.practices.length}</div>
                  <div className="text-purple-600 text-xs">Practices</div>
                </div>
                <div className="text-center p-2 bg-teal-50 rounded">
                  <div className="font-semibold text-teal-900">{subdomain.data_elements.length}</div>
                  <div className="text-teal-600 text-xs">Data</div>
                </div>
                <div className="text-center p-2 bg-yellow-50 rounded">
                  <div className="font-semibold text-yellow-900">{subdomain.hypotheses.length}</div>
                  <div className="text-yellow-600 text-xs">Hypotheses</div>
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t border-neutral-200">
                {/* Action Buttons */}
                <div className="flex gap-3 p-4 bg-neutral-50 border-b border-neutral-200">
                  <button
                    onClick={() => onEditClick(subdomain)}
                    className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Subdomain
                  </button>
                  <button
                    onClick={() => onResearchClick(subdomain)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                  >
                    <Brain className="w-4 h-4" />
                    Run Research
                  </button>
                  <button
                    onClick={() => handleDiscoverRelationships(subdomain.subdomain_id)}
                    disabled={discoveringRelationships.has(subdomain.subdomain_id)}
                    className="px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {discoveringRelationships.has(subdomain.subdomain_id) ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Discovering...
                      </>
                    ) : (
                      <>
                        <Link className="w-4 h-4" />
                        Discover Relationships
                      </>
                    )}
                  </button>
                </div>

                {/* Relationships Summary Section */}
                {(subdomainRelationships[subdomain.subdomain_id]?.length > 0 || loadingRelationships.has(subdomain.subdomain_id)) && (
                  <CollapsibleSection
                    title="Relationships"
                    count={subdomainRelationships[subdomain.subdomain_id]?.length || 0}
                    icon={Link}
                    color="purple"
                    isExpanded={expandedSections.has(`${subdomain.subdomain_id}-relationships`)}
                    onToggle={() => toggleSection(`${subdomain.subdomain_id}-relationships`)}
                  >
                    {loadingRelationships.has(subdomain.subdomain_id) ? (
                      <div className="flex items-center justify-center py-4 text-neutral-500">
                        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                        Loading relationships...
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Group relationships by type */}
                        {Object.entries(
                          (subdomainRelationships[subdomain.subdomain_id] || []).reduce((acc, rel) => {
                            acc[rel.relationshipType] = acc[rel.relationshipType] || [];
                            acc[rel.relationshipType].push(rel);
                            return acc;
                          }, {} as Record<string, ResearchRelationship[]>)
                        ).map(([type, rels]) => {
                          const typeInfo = getRelationshipTypeInfo(type);
                          return (
                            <div key={type} className="mb-4">
                              <div className={`text-xs font-semibold px-2 py-1 rounded mb-2 inline-block ${typeInfo.color}`}>
                                {typeInfo.icon} {typeInfo.label} ({rels.length})
                              </div>
                              <div className="space-y-1 ml-2">
                                {rels.map((rel) => (
                                  <div key={rel.relationshipId} className="text-sm p-2 bg-white rounded border border-neutral-200 flex items-center gap-2">
                                    <span className="font-medium text-neutral-700">{rel.sourceTitle}</span>
                                    <ArrowRight className="w-3 h-3 text-neutral-400" />
                                    <span className="font-medium text-neutral-700">{rel.targetTitle}</span>
                                    {rel.strength && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        rel.strength === 'high' ? 'bg-green-100 text-green-700' :
                                        rel.strength === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-neutral-100 text-neutral-600'
                                      }`}>
                                        {rel.strength}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {(subdomainRelationships[subdomain.subdomain_id]?.length || 0) === 0 && (
                          <p className="text-sm text-neutral-500 text-center py-4">
                            No relationships discovered yet. Click "Discover Relationships" to analyze connections.
                          </p>
                        )}
                      </div>
                    )}
                  </CollapsibleSection>
                )}

                {/* Trends Section */}
                {subdomain.trends.length > 0 && (
                  <CollapsibleSection
                    title="Trends"
                    count={subdomain.trends.length}
                    icon={TrendingUp}
                    color="blue"
                    isExpanded={expandedSections.has(`${subdomain.subdomain_id}-trends`)}
                    onToggle={() => toggleSection(`${subdomain.subdomain_id}-trends`)}
                  >
                    <div className="space-y-3">
                      {subdomain.trends.map((trend) => (
                        <div key={trend.trend_id} className="p-4 bg-white rounded-lg border border-neutral-200">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-neutral-900">{trend.title}</h4>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {trend.time_horizon}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-600 mb-2">{trend.description}</p>
                          <p className="text-sm text-blue-800 bg-blue-50 p-2 rounded">
                            <strong>Impact:</strong> {trend.impact}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Issues Section */}
                {subdomain.issues.length > 0 && (
                  <CollapsibleSection
                    title="Issues"
                    count={subdomain.issues.length}
                    icon={AlertTriangle}
                    color="orange"
                    isExpanded={expandedSections.has(`${subdomain.subdomain_id}-issues`)}
                    onToggle={() => toggleSection(`${subdomain.subdomain_id}-issues`)}
                  >
                    <div className="space-y-3">
                      {subdomain.issues.map((issue) => (
                        <div key={issue.issue_id} className="p-4 bg-white rounded-lg border border-neutral-200">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-neutral-900">{issue.title}</h4>
                            <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(issue.severity)}`}>
                              {issue.severity.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-600 mb-2">{issue.description}</p>
                          {issue.root_causes.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-semibold text-neutral-700 mb-1">Root Causes:</p>
                              <ul className="list-disc list-inside text-sm text-neutral-600">
                                {issue.root_causes.slice(0, 3).map((cause, idx) => (
                                  <li key={idx}>{cause}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {issue.business_impact && (
                            <p className="text-sm text-orange-800 bg-orange-50 p-2 rounded">
                              <strong>Business Impact:</strong> {issue.business_impact}
                            </p>
                          )}
                          {/* Issue Resolution Canvas Button */}
                          {onOpenIssueCanvas && (
                            <div className="mt-3 pt-3 border-t border-neutral-100">
                              <button
                                onClick={() => onOpenIssueCanvas(issue, subdomain)}
                                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 px-3 py-1.5 rounded-lg transition-colors"
                              >
                                <Network className="w-4 h-4" />
                                View Resolution Canvas
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Best Practices Section */}
                {subdomain.practices.length > 0 && (
                  <CollapsibleSection
                    title="Best Practices"
                    count={subdomain.practices.length}
                    icon={Award}
                    color="purple"
                    isExpanded={expandedSections.has(`${subdomain.subdomain_id}-practices`)}
                    onToggle={() => toggleSection(`${subdomain.subdomain_id}-practices`)}
                  >
                    <div className="space-y-3">
                      {subdomain.practices.map((practice) => (
                        <div key={practice.practice_id} className="p-4 bg-white rounded-lg border border-neutral-200">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-neutral-900">{practice.title}</h4>
                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {practice.complexity} complexity
                            </span>
                          </div>
                          <p className="text-sm text-neutral-600 mb-2">{practice.description}</p>
                          {practice.implementation_steps.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-semibold text-neutral-700 mb-1">Implementation Steps:</p>
                              <ol className="list-decimal list-inside text-sm text-neutral-600">
                                {practice.implementation_steps.slice(0, 3).map((step, idx) => (
                                  <li key={idx}>{step}</li>
                                ))}
                                {practice.implementation_steps.length > 3 && (
                                  <li className="text-neutral-500">+{practice.implementation_steps.length - 3} more steps</li>
                                )}
                              </ol>
                            </div>
                          )}
                          {practice.expected_benefits.length > 0 && (
                            <p className="text-sm text-green-800 bg-green-50 p-2 rounded">
                              <strong>Benefits:</strong> {practice.expected_benefits.slice(0, 2).join(', ')}
                              {practice.expected_benefits.length > 2 && ` +${practice.expected_benefits.length - 2} more`}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Data Elements Section */}
                {subdomain.data_elements.length > 0 && (
                  <CollapsibleSection
                    title="Data Elements"
                    count={subdomain.data_elements.length}
                    icon={Database}
                    color="teal"
                    isExpanded={expandedSections.has(`${subdomain.subdomain_id}-data`)}
                    onToggle={() => toggleSection(`${subdomain.subdomain_id}-data`)}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {subdomain.data_elements.map((element) => (
                        <div key={element.element_id} className="p-4 bg-white rounded-lg border border-neutral-200">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-neutral-900">{element.name}</h4>
                            <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded">
                              {element.data_type}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-600 mb-2">{element.description}</p>
                          <div className="flex gap-2 text-xs">
                            {element.source_system && (
                              <span className="px-2 py-1 bg-neutral-100 text-neutral-600 rounded">
                                Source: {element.source_system}
                              </span>
                            )}
                            <span className="px-2 py-1 bg-neutral-100 text-neutral-600 rounded">
                              {element.criticality} criticality
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Hypotheses Section */}
                {subdomain.hypotheses.length > 0 && (
                  <CollapsibleSection
                    title="Hypotheses"
                    count={subdomain.hypotheses.length}
                    icon={Lightbulb}
                    color="yellow"
                    isExpanded={expandedSections.has(`${subdomain.subdomain_id}-hypotheses`)}
                    onToggle={() => toggleSection(`${subdomain.subdomain_id}-hypotheses`)}
                  >
                    <div className="space-y-3">
                      {subdomain.hypotheses.map((hypothesis) => (
                        <div key={hypothesis.hypothesis_id} className="p-4 bg-white rounded-lg border border-neutral-200">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-neutral-900">{hypothesis.title}</h4>
                            <div className="flex gap-2">
                              <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                                {hypothesis.hypothesis_type}
                              </span>
                              {hypothesis.typical_success_rate && (
                                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                  {(hypothesis.typical_success_rate * 100).toFixed(0)}% success
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-sm font-medium text-neutral-800 mb-2">{hypothesis.statement}</p>
                          <p className="text-sm text-neutral-600 mb-3">{hypothesis.rationale}</p>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {hypothesis.required_data.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-neutral-700 mb-1">Required Data:</p>
                                <ul className="list-disc list-inside text-neutral-600">
                                  {hypothesis.required_data.slice(0, 3).map((data, idx) => (
                                    <li key={idx} className="text-xs">{data}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {hypothesis.validation_criteria.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-neutral-700 mb-1">Validation Criteria:</p>
                                <ul className="list-disc list-inside text-neutral-600">
                                  {hypothesis.validation_criteria.slice(0, 3).map((criteria, idx) => (
                                    <li key={idx} className="text-xs">{criteria}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          {hypothesis.test_approach && (
                            <p className="text-sm text-yellow-800 bg-yellow-50 p-2 rounded mt-3">
                              <strong>Test Approach:</strong> {hypothesis.test_approach}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Empty State */}
                {subdomain.trends.length === 0 && subdomain.issues.length === 0 &&
                 subdomain.practices.length === 0 && subdomain.data_elements.length === 0 &&
                 subdomain.hypotheses.length === 0 && (
                  <div className="p-8 text-center text-neutral-500">
                    <Brain className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                    <p className="font-medium">No research data yet</p>
                    <p className="text-sm mt-1">Click "Run Research" to generate insights for this subdomain</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string;
  count: number;
  icon: React.ComponentType<any>;
  color: 'blue' | 'orange' | 'purple' | 'teal' | 'yellow';
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  count,
  icon: Icon,
  color,
  isExpanded,
  onToggle,
  children,
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  };

  return (
    <div className="border-b border-neutral-200 last:border-b-0">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors ${colorClasses[color]} border-l-4`}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <Icon className="w-5 h-5" />
          <span className="font-semibold">{title}</span>
          <span className="text-sm opacity-75">({count})</span>
        </div>
      </button>
      {isExpanded && (
        <div className="p-4 bg-neutral-50">
          {children}
        </div>
      )}
    </div>
  );
};

// Trends Tab
const TrendsTab: React.FC<{ subdomains: Subdomain[] }> = ({ subdomains }) => {
  const allTrends = subdomains.flatMap((sub) =>
    sub.trends.map((trend) => ({ ...trend, subdomain: sub.name }))
  );

  return (
    <div className="space-y-4">
      {allTrends.map((trend) => (
        <div
          key={trend.trend_id}
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-1">{trend.title}</h3>
              <span className="inline-block text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                {trend.subdomain}
              </span>
            </div>
            <div className="text-sm text-neutral-600">{trend.time_horizon}</div>
          </div>
          <p className="text-neutral-700 mb-3">{trend.description}</p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-3">
            <p className="text-sm text-blue-900">
              <strong>Impact:</strong> {trend.impact}
            </p>
          </div>
          {trend.sources.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-neutral-600 hover:text-neutral-900">
                Sources ({trend.sources.length})
              </summary>
              <ul className="mt-2 ml-4 list-disc text-neutral-600">
                {trend.sources.map((source, idx) => (
                  <li key={idx}>{source}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </div>
  );
};

// Issues Tab - Similar pattern for issues
const IssuesTab: React.FC<{ subdomains: Subdomain[] }> = ({ subdomains }) => {
  const allIssues = subdomains.flatMap((sub) =>
    sub.issues.map((issue) => ({ ...issue, subdomain: sub.name }))
  );

  const getSeverityColor = (severity: string) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-blue-100 text-blue-800 border-blue-300',
    };
    return colors[severity as keyof typeof colors] || colors.medium;
  };

  return (
    <div className="space-y-4">
      {allIssues.map((issue) => (
        <div
          key={issue.issue_id}
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">{issue.title}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                  {issue.subdomain}
                </span>
                <span
                  className={`text-xs px-2 py-1 rounded border ${getSeverityColor(
                    issue.severity
                  )}`}
                >
                  {issue.severity.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <p className="text-neutral-700 mb-3">{issue.description}</p>

          {issue.root_causes.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-neutral-800 mb-1">Root Causes:</h4>
              <ul className="list-disc list-inside text-sm text-neutral-600 space-y-1">
                {issue.root_causes.map((cause, idx) => (
                  <li key={idx}>{cause}</li>
                ))}
              </ul>
            </div>
          )}

          {issue.business_impact && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3">
              <p className="text-sm text-red-900">
                <strong>Business Impact:</strong> {issue.business_impact}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Best Practices Tab
const PracticesTab: React.FC<{ subdomains: Subdomain[] }> = ({ subdomains }) => {
  const allPractices = subdomains.flatMap((sub) =>
    sub.practices.map((practice) => ({ ...practice, subdomain: sub.name }))
  );

  return (
    <div className="space-y-4">
      {allPractices.map((practice) => (
        <div
          key={practice.practice_id}
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">{practice.title}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                  {practice.subdomain}
                </span>
                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                  {practice.complexity} complexity
                </span>
              </div>
            </div>
            {practice.estimated_effort && (
              <div className="text-sm text-neutral-600">{practice.estimated_effort}</div>
            )}
          </div>
          <p className="text-neutral-700 mb-4">{practice.description}</p>

          {practice.implementation_steps.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-neutral-800 mb-2">
                Implementation Steps:
              </h4>
              <ol className="list-decimal list-inside text-sm text-neutral-600 space-y-1">
                {practice.implementation_steps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          {practice.expected_benefits.length > 0 && (
            <div className="bg-green-50 border-l-4 border-green-500 p-3">
              <h4 className="text-sm font-semibold text-green-900 mb-1">Expected Benefits:</h4>
              <ul className="list-disc list-inside text-sm text-green-800 space-y-1">
                {practice.expected_benefits.map((benefit, idx) => (
                  <li key={idx}>{benefit}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Data Elements Tab
const DataElementsTab: React.FC<{ subdomains: Subdomain[] }> = ({ subdomains }) => {
  const allDataElements = subdomains.flatMap((sub) =>
    sub.data_elements.map((element) => ({ ...element, subdomain: sub.name }))
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {allDataElements.map((element) => (
        <div
          key={element.element_id}
          className="bg-white rounded-lg border border-neutral-200 p-4"
        >
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-base font-semibold text-neutral-900">{element.name}</h3>
            <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded">
              {element.data_type}
            </span>
          </div>
          <p className="text-sm text-neutral-600 mb-3">{element.description}</p>
          <div className="text-xs text-neutral-500 space-y-1">
            <p>
              <strong>Subdomain:</strong> {element.subdomain}
            </p>
            {element.source_system && (
              <p>
                <strong>Source:</strong> {element.source_system}
              </p>
            )}
            <p>
              <strong>Criticality:</strong> {element.criticality}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Hypotheses Tab
const HypothesesTab: React.FC<{ subdomains: Subdomain[] }> = ({ subdomains }) => {
  const allHypotheses = subdomains.flatMap((sub) =>
    sub.hypotheses.map((hypothesis) => ({ ...hypothesis, subdomain: sub.name }))
  );

  return (
    <div className="space-y-4">
      {allHypotheses.map((hypothesis) => (
        <div
          key={hypothesis.hypothesis_id}
          className="bg-white rounded-lg border border-neutral-200 p-6"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">{hypothesis.title}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                  {hypothesis.subdomain}
                </span>
                <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                  {hypothesis.hypothesis_type}
                </span>
              </div>
            </div>
            {hypothesis.typical_success_rate && (
              <div className="text-sm text-neutral-600">
                Success Rate: {(hypothesis.typical_success_rate * 100).toFixed(0)}%
              </div>
            )}
          </div>

          <p className="text-neutral-700 mb-3 font-medium">{hypothesis.statement}</p>
          <p className="text-sm text-neutral-600 mb-4">{hypothesis.rationale}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="text-sm font-semibold text-neutral-800 mb-2">Required Data:</h4>
              <ul className="list-disc list-inside text-sm text-neutral-600 space-y-1">
                {hypothesis.required_data.map((data, idx) => (
                  <li key={idx}>{data}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-neutral-800 mb-2">
                Validation Criteria:
              </h4>
              <ul className="list-disc list-inside text-sm text-neutral-600 space-y-1">
                {hypothesis.validation_criteria.map((criteria, idx) => (
                  <li key={idx}>{criteria}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3">
            <p className="text-sm text-yellow-900">
              <strong>Test Approach:</strong> {hypothesis.test_approach}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Statistics Tab
const StatisticsTab: React.FC<{ domainPack: DomainPackDetailType; statistics?: StatsType }> = ({
  domainPack,
  statistics,
}) => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg border border-neutral-200 p-6">
      <h2 className="text-xl font-semibold text-neutral-900 mb-4">Usage Statistics</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-primary-50 rounded-lg">
          <div className="text-3xl font-bold text-primary-600">
            {domainPack.case_application_count}
          </div>
          <div className="text-sm text-neutral-600 mt-1">Case Applications</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-3xl font-bold text-green-600">
            {domainPack.hypothesis_count}
          </div>
          <div className="text-sm text-neutral-600 mt-1">Total Hypotheses</div>
        </div>
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-3xl font-bold text-blue-600">{domainPack.total_tokens || 0}</div>
          <div className="text-sm text-neutral-600 mt-1">Tokens Used</div>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <div className="text-3xl font-bold text-purple-600">
            {(domainPack.quality_score * 100).toFixed(0)}%
          </div>
          <div className="text-sm text-neutral-600 mt-1">Quality Score</div>
        </div>
      </div>
    </div>

    {statistics && (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Component Breakdown</h2>
        <div className="space-y-3">
          <ProgressBar
            label="Trends"
            value={statistics.statistics.trends}
            color="bg-blue-500"
          />
          <ProgressBar
            label="Issues"
            value={statistics.statistics.issues}
            color="bg-orange-500"
          />
          <ProgressBar
            label="Best Practices"
            value={statistics.statistics.best_practices}
            color="bg-purple-500"
          />
          <ProgressBar
            label="Data Elements"
            value={statistics.statistics.data_elements}
            color="bg-teal-500"
          />
          <ProgressBar
            label="Hypotheses"
            value={statistics.statistics.hypotheses}
            color="bg-yellow-500"
          />
        </div>
      </div>
    )}
  </div>
);

const ProgressBar: React.FC<{ label: string; value: number; color: string }> = ({
  label,
  value,
  color,
}) => {
  const maxValue = 50; // Reasonable max for visualization
  const percentage = Math.min((value / maxValue) * 100, 100);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-neutral-700">{label}</span>
        <span className="text-sm font-semibold text-neutral-900">{value}</span>
      </div>
      <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

// Knowledge Graph Canvas Component - Force-directed visualization
interface GraphNode {
  id: string;
  label: string;
  type: string;
  color: string;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface GraphLink {
  source: string;
  target: string;
  type: string;
  strength: string;
  explanation: string;
}

const KnowledgeGraphCanvas: React.FC<{
  nodes: GraphNode[];
  links: GraphLink[];
  selectedNode: string | null;
  onNodeClick: (nodeId: string) => void;
  relationshipColors: Record<string, string>;
  focusedNode?: string | null;
  hiddenRelTypes?: Set<string>;
  hiddenNodeTypes?: Set<string>;
  showDetails?: boolean;
}> = ({ nodes, links, selectedNode, onNodeClick, relationshipColors, focusedNode, hiddenRelTypes, hiddenNodeTypes, showDetails = false }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = React.useState({ width: 800, height: 600 });
  const transformRef = React.useRef({ x: 0, y: 0, scale: 1 });
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const dragStateRef = React.useRef<{
    isDraggingNode: boolean;
    isPanning: boolean;
    draggedNode: string | null;
    lastPanPos: { x: number; y: number };
  }>({
    isDraggingNode: false,
    isPanning: false,
    draggedNode: null,
    lastPanPos: { x: 0, y: 0 },
  });
  const [hoveredNode, setHoveredNode] = React.useState<string | null>(null);
  const nodesRef = React.useRef<GraphNode[]>([]);
  const animationRef = React.useRef<number>(0);

  // Determine which nodes/links to show based on focus and filters
  const getVisibleData = React.useCallback(() => {
    // Start with all links, then filter
    let filteredLinks = links;

    // Filter by relationship type
    if (hiddenRelTypes && hiddenRelTypes.size > 0) {
      filteredLinks = filteredLinks.filter(link => !hiddenRelTypes.has(link.type));
    }

    if (!focusedNode) {
      // No focus - show all nodes (filtered by type) and filtered links
      let visibleNodeIds = new Set(nodes.map(n => n.id));

      // Filter nodes by type
      if (hiddenNodeTypes && hiddenNodeTypes.size > 0) {
        visibleNodeIds = new Set(nodes.filter(n => !hiddenNodeTypes.has(n.type)).map(n => n.id));
      }

      // Only show links where both endpoints are visible
      const visibleLinks = filteredLinks.filter(link =>
        visibleNodeIds.has(link.source) && visibleNodeIds.has(link.target)
      );

      return { visibleNodes: visibleNodeIds, visibleLinks };
    }

    // Focused mode - show focused node and connected nodes
    const connectedNodes = new Set<string>([focusedNode]);
    const visibleLinks = filteredLinks.filter(link => {
      if (link.source === focusedNode || link.target === focusedNode) {
        const otherNodeId = link.source === focusedNode ? link.target : link.source;
        const otherNode = nodes.find(n => n.id === otherNodeId);

        // Check if the other node type is hidden
        if (hiddenNodeTypes && otherNode && hiddenNodeTypes.has(otherNode.type)) {
          return false;
        }

        connectedNodes.add(link.source);
        connectedNodes.add(link.target);
        return true;
      }
      return false;
    });

    return { visibleNodes: connectedNodes, visibleLinks };
  }, [focusedNode, nodes, links, hiddenRelTypes, hiddenNodeTypes]);

  // Zoom controls
  const zoomIn = () => {
    transformRef.current.scale = Math.min(3, transformRef.current.scale * 1.2);
    forceUpdate();
  };

  const zoomOut = () => {
    transformRef.current.scale = Math.max(0.3, transformRef.current.scale / 1.2);
    forceUpdate();
  };

  const resetView = () => {
    transformRef.current = { x: 0, y: 0, scale: 1 };
    forceUpdate();
  };

  const fitToView = () => {
    if (nodesRef.current.length === 0) return;

    const { visibleNodes } = getVisibleData();
    const visibleNodesList = nodesRef.current.filter(n => visibleNodes.has(n.id));
    if (visibleNodesList.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    visibleNodesList.forEach(node => {
      if (node.x && node.y) {
        minX = Math.min(minX, node.x);
        minY = Math.min(minY, node.y);
        maxX = Math.max(maxX, node.x);
        maxY = Math.max(maxY, node.y);
      }
    });

    const padding = 100;
    const graphWidth = maxX - minX + padding * 2;
    const graphHeight = maxY - minY + padding * 2;

    const scaleX = dimensions.width / graphWidth;
    const scaleY = dimensions.height / graphHeight;
    const newScale = Math.min(scaleX, scaleY, 2) * 0.85;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    transformRef.current = {
      x: dimensions.width / 2 - centerX * newScale,
      y: dimensions.height / 2 - centerY * newScale,
      scale: newScale,
    };
    forceUpdate();
  };

  // Store links in a ref for the animation loop
  const linksRef = React.useRef(links);
  linksRef.current = links;

  // Store drawGraph in a ref to avoid stale closure in animation loop
  const drawGraphRef = React.useRef<() => void>(() => {});

  // Create a stable key from node IDs
  const nodesKey = React.useMemo(() => nodes.map(n => n.id).sort().join(','), [nodes]);

  // Initialize node positions with force simulation
  React.useEffect(() => {
    if (nodes.length === 0) return;

    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;

    // Initialize positions - spread nodes more
    nodesRef.current = nodes.map((node, i) => {
      const angle = (i / nodes.length) * 2 * Math.PI;
      const radius = Math.min(dimensions.width, dimensions.height) * 0.35;
      return {
        ...node,
        x: centerX + radius * Math.cos(angle) + (Math.random() - 0.5) * 100,
        y: centerY + radius * Math.sin(angle) + (Math.random() - 0.5) * 100,
        vx: 0,
        vy: 0,
      };
    });

    // Run initial simulation iterations
    for (let i = 0; i < 150; i++) {
      simulateStepWithLinks(centerX, centerY, linksRef.current);
    }

    // Start animation loop - use ref to get latest drawGraph
    const animate = () => {
      simulateStepWithLinks(centerX, centerY, linksRef.current);
      drawGraphRef.current(); // Use ref to always get latest version
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [nodesKey, dimensions.width, dimensions.height]);

  // Force simulation step - accepts links as parameter to avoid stale closure
  const simulateStepWithLinks = (centerX: number, centerY: number, currentLinks: GraphLink[]) => {
    const alpha = 0.1;
    const repulsion = 15000;
    const linkDistance = 220;
    const linkStrength = 0.15;
    const centerStrength = 0.003;
    const dragState = dragStateRef.current;

    nodesRef.current.forEach((node) => {
      if (!node.x || !node.y) return;

      // Center gravity
      node.vx = (node.vx || 0) + (centerX - node.x) * centerStrength;
      node.vy = (node.vy || 0) + (centerY - node.y) * centerStrength;

      // Node repulsion
      nodesRef.current.forEach((other) => {
        if (node.id === other.id || !other.x || !other.y) return;
        const dx = node.x! - other.x;
        const dy = node.y! - other.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = repulsion / (dist * dist);
        node.vx! += (dx / dist) * force * alpha;
        node.vy! += (dy / dist) * force * alpha;
      });
    });

    // Link attraction
    currentLinks.forEach((link) => {
      const source = nodesRef.current.find((n) => n.id === link.source);
      const target = nodesRef.current.find((n) => n.id === link.target);
      if (!source || !target || !source.x || !source.y || !target.x || !target.y) return;

      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = (dist - linkDistance) * linkStrength * alpha;

      source.vx! += (dx / dist) * force;
      source.vy! += (dy / dist) * force;
      target.vx! -= (dx / dist) * force;
      target.vy! -= (dy / dist) * force;
    });

    // Apply velocities
    nodesRef.current.forEach((node) => {
      // Don't move the node being dragged
      if (dragState.isDraggingNode && node.id === dragState.draggedNode) return;

      node.x! += node.vx! * 0.85;
      node.y! += node.vy! * 0.85;
      node.vx! *= 0.85;
      node.vy! *= 0.85;

      // Keep within bounds
      const padding = 80;
      node.x = Math.max(padding, Math.min(dimensions.width - padding, node.x!));
      node.y = Math.max(padding, Math.min(dimensions.height - padding, node.y!));
    });
  };

  // Set dimensions from container - with retry for initial render
  React.useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        // Only update if we have valid dimensions
        if (clientWidth > 0 && clientHeight > 0) {
          setDimensions({ width: clientWidth, height: clientHeight });
        }
      }
    };

    // Initial update with small delay to ensure container is rendered
    updateSize();
    const timeoutId = setTimeout(updateSize, 100);

    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Draw the graph
  const drawGraph = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const transform = transformRef.current;
    const { visibleNodes, visibleLinks } = getVisibleData();

    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height);

    // Apply transform
    ctx.save();
    ctx.translate(transform.x, transform.y);
    ctx.scale(transform.scale, transform.scale);

    // Draw links
    visibleLinks.forEach((link) => {
      const source = nodesRef.current.find((n) => n.id === link.source);
      const target = nodesRef.current.find((n) => n.id === link.target);
      if (!source || !target || !source.x || !source.y || !target.x || !target.y) return;

      const isHighlighted =
        selectedNode === link.source ||
        selectedNode === link.target ||
        hoveredNode === link.source ||
        hoveredNode === link.target ||
        focusedNode === link.source ||
        focusedNode === link.target;

      ctx.beginPath();
      ctx.moveTo(source.x, source.y);

      // Curved line
      const midX = (source.x + target.x) / 2;
      const midY = (source.y + target.y) / 2;
      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const cpx = midX - dy * 0.15;
      const cpy = midY + dx * 0.15;
      ctx.quadraticCurveTo(cpx, cpy, target.x, target.y);

      ctx.strokeStyle = isHighlighted
        ? relationshipColors[link.type] || '#6b7280'
        : (relationshipColors[link.type] || '#6b7280') + '60';
      ctx.lineWidth = link.strength === 'high' ? 3 : link.strength === 'medium' ? 2 : 1;
      ctx.stroke();

      // Draw arrow
      const angle = Math.atan2(target.y - cpy, target.x - cpx);
      const arrowSize = 8;
      ctx.beginPath();
      ctx.moveTo(
        target.x - 20 * Math.cos(angle),
        target.y - 20 * Math.sin(angle)
      );
      ctx.lineTo(
        target.x - 20 * Math.cos(angle) - arrowSize * Math.cos(angle - Math.PI / 6),
        target.y - 20 * Math.sin(angle) - arrowSize * Math.sin(angle - Math.PI / 6)
      );
      ctx.moveTo(
        target.x - 20 * Math.cos(angle),
        target.y - 20 * Math.sin(angle)
      );
      ctx.lineTo(
        target.x - 20 * Math.cos(angle) - arrowSize * Math.cos(angle + Math.PI / 6),
        target.y - 20 * Math.sin(angle) - arrowSize * Math.sin(angle + Math.PI / 6)
      );
      ctx.stroke();

      // Draw relationship label on highlighted links
      if (isHighlighted) {
        ctx.font = '10px Inter, sans-serif';
        ctx.fillStyle = relationshipColors[link.type] || '#6b7280';
        ctx.textAlign = 'center';
        ctx.fillText(link.type.replace(/_/g, ' '), cpx, cpy - 5);
      }
    });

    // Draw nodes
    nodesRef.current.forEach((node) => {
      if (!node.x || !node.y) return;
      if (!visibleNodes.has(node.id)) return; // Skip hidden nodes

      const isSelected = selectedNode === node.id;
      const isHovered = hoveredNode === node.id;
      const isFocused = focusedNode === node.id;
      const isConnected =
        selectedNode &&
        links.some(
          (l) =>
            (l.source === selectedNode && l.target === node.id) ||
            (l.target === selectedNode && l.source === node.id)
        );

      const radius = isSelected || isHovered || isFocused ? 20 : 14;
      const opacity = selectedNode && !isSelected && !isConnected ? 0.3 : 1;

      // Node shadow
      ctx.beginPath();
      ctx.arc(node.x + 2, node.y + 2, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0, 0, 0, ${0.15 * opacity})`;
      ctx.fill();

      // Node circle
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = node.color + (opacity < 1 ? '50' : 'ff');
      ctx.fill();
      ctx.strokeStyle = isFocused ? '#f59e0b' : isSelected ? '#1f2937' : '#ffffff';
      ctx.lineWidth = isFocused ? 4 : isSelected ? 3 : 2;
      ctx.stroke();

      // Node label - show expanded details when showDetails is true
      if (showDetails) {
        // Expanded detail view
        const maxWidth = 180;
        const padding = 8;
        const lineHeight = 14;

        // Word wrap function
        const wrapText = (text: string, maxW: number): string[] => {
          const words = text.split(' ');
          const lines: string[] = [];
          let currentLine = '';

          ctx.font = '11px Inter, sans-serif';
          words.forEach(word => {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            if (ctx.measureText(testLine).width > maxW) {
              if (currentLine) lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          });
          if (currentLine) lines.push(currentLine);
          return lines.slice(0, 4); // Max 4 lines
        };

        const titleLines = wrapText(node.label, maxWidth - padding * 2);
        const typeText = node.type === 'BestPractice' ? 'Best Practice' : node.type;

        // Calculate box dimensions
        ctx.font = 'bold 11px Inter, sans-serif';
        const titleWidth = Math.max(...titleLines.map(l => ctx.measureText(l).width));
        ctx.font = '10px Inter, sans-serif';
        const typeWidth = ctx.measureText(typeText).width;
        const boxWidth = Math.min(maxWidth, Math.max(titleWidth, typeWidth) + padding * 2);
        const boxHeight = (titleLines.length + 1) * lineHeight + padding * 2;
        const boxX = node.x - boxWidth / 2;
        const boxY = node.y + radius + 8;

        // Draw box background
        ctx.fillStyle = `rgba(255, 255, 255, ${0.98 * opacity})`;
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 6);
        ctx.fill();
        ctx.strokeStyle = isFocused ? '#f59e0b' : node.color;
        ctx.lineWidth = isFocused ? 2 : 1.5;
        ctx.stroke();

        // Draw type label at top
        ctx.font = 'bold 9px Inter, sans-serif';
        ctx.fillStyle = node.color;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(typeText.toUpperCase(), boxX + padding, boxY + padding);

        // Draw title lines
        ctx.font = '11px Inter, sans-serif';
        ctx.fillStyle = `rgba(31, 41, 55, ${opacity})`;
        titleLines.forEach((line, i) => {
          ctx.fillText(line, boxX + padding, boxY + padding + lineHeight + i * lineHeight);
        });

      } else {
        // Compact view (original)
        ctx.font = `${isSelected || isHovered || isFocused ? 'bold ' : ''}11px Inter, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const label = node.label.length > 25 ? node.label.substring(0, 25) + '...' : node.label;
        const labelWidth = ctx.measureText(label).width + 10;
        const labelY = node.y + radius + 14;

        ctx.fillStyle = `rgba(255, 255, 255, ${0.95 * opacity})`;
        ctx.beginPath();
        ctx.roundRect(node.x - labelWidth / 2, labelY - 9, labelWidth, 18, 4);
        ctx.fill();
        ctx.strokeStyle = isFocused ? '#f59e0b' : `rgba(229, 231, 235, ${opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.fillStyle = `rgba(55, 65, 81, ${opacity})`;
        ctx.fillText(label, node.x, labelY);
      }

      // Type badge on node
      const typeLabel = node.type.substring(0, 1).toUpperCase();
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(typeLabel, node.x, node.y);
    });

    ctx.restore();
  }, [links, selectedNode, hoveredNode, focusedNode, dimensions, relationshipColors, getVisibleData, showDetails]);

  // Keep drawGraph ref updated for animation loop
  drawGraphRef.current = drawGraph;

  // Mouse handlers using refs to avoid stale closure issues
  const getMousePos = (clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const transform = transformRef.current;
    return {
      x: (clientX - rect.left - transform.x) / transform.scale,
      y: (clientY - rect.top - transform.y) / transform.scale,
    };
  };

  const getScreenPos = (clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const findNodeAt = (x: number, y: number) => {
    const { visibleNodes } = getVisibleData();
    // Larger hit radius for easier selection (25px base)
    const hitRadius = 25;
    return nodesRef.current.find((node) => {
      if (!node.x || !node.y) return false;
      if (!visibleNodes.has(node.id)) return false;
      const dx = x - node.x;
      const dy = y - node.y;
      return Math.sqrt(dx * dx + dy * dy) < hitRadius;
    });
  };

  // Track drag start position for click detection
  const dragStartPosRef = React.useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const pos = getMousePos(e.clientX, e.clientY);
    const screenPos = getScreenPos(e.clientX, e.clientY);
    const node = findNodeAt(pos.x, pos.y);
    const dragState = dragStateRef.current;

    // Store start position for click detection
    dragStartPosRef.current = screenPos;

    if (node) {
      dragState.draggedNode = node.id;
      dragState.isDraggingNode = true;
      // Stop the node's velocity when grabbed
      const nodeData = nodesRef.current.find(n => n.id === node.id);
      if (nodeData) {
        nodeData.vx = 0;
        nodeData.vy = 0;
      }
    } else {
      dragState.isPanning = true;
      dragState.lastPanPos = screenPos;
    }
    forceUpdate();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const pos = getMousePos(e.clientX, e.clientY);
    const screenPos = getScreenPos(e.clientX, e.clientY);
    const dragState = dragStateRef.current;

    if (dragState.isDraggingNode && dragState.draggedNode) {
      const node = nodesRef.current.find((n) => n.id === dragState.draggedNode);
      if (node) {
        node.x = pos.x;
        node.y = pos.y;
        node.vx = 0;
        node.vy = 0;
      }
    } else if (dragState.isPanning) {
      const dx = screenPos.x - dragState.lastPanPos.x;
      const dy = screenPos.y - dragState.lastPanPos.y;
      transformRef.current.x += dx;
      transformRef.current.y += dy;
      dragState.lastPanPos = screenPos;
    } else {
      const node = findNodeAt(pos.x, pos.y);
      setHoveredNode(node?.id || null);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    const dragState = dragStateRef.current;
    const screenPos = getScreenPos(e.clientX, e.clientY);
    const startPos = dragStartPosRef.current;

    // Check if this was a click (minimal movement) vs drag
    const isClick = startPos &&
      Math.abs(screenPos.x - startPos.x) < 5 &&
      Math.abs(screenPos.y - startPos.y) < 5;

    if (dragState.draggedNode && isClick) {
      // This was a click on a node
      onNodeClick(dragState.draggedNode);
    }

    dragState.isDraggingNode = false;
    dragState.isPanning = false;
    dragState.draggedNode = null;
    dragStartPosRef.current = null;
    forceUpdate();
  };

  // Non-passive wheel event listener to allow preventDefault
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const scaleFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const transform = transformRef.current;
      const newScale = Math.max(0.3, Math.min(3, transform.scale * scaleFactor));

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      transformRef.current = {
        x: mouseX - (mouseX - transform.x) * (newScale / transform.scale),
        y: mouseY - (mouseY - transform.y) * (newScale / transform.scale),
        scale: newScale,
      };
      forceUpdate();
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, []);

  // Cursor style based on state
  const getCursor = () => {
    const dragState = dragStateRef.current;
    if (dragState.isDraggingNode) return 'grabbing';
    if (dragState.isPanning) return 'grabbing';
    if (hoveredNode) return 'pointer';
    return 'grab';
  };

  return (
    <div ref={containerRef} className="w-full h-full relative" style={{ minHeight: '100%' }}>
      {/* Zoom Controls */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1 bg-white/90 backdrop-blur rounded-lg shadow-lg border border-gray-200 p-1">
        <button
          onClick={zoomIn}
          className="p-2 hover:bg-gray-100 rounded text-gray-700 font-bold text-lg"
          title="Zoom In"
        >
          +
        </button>
        <div className="border-t border-gray-200" />
        <button
          onClick={zoomOut}
          className="p-2 hover:bg-gray-100 rounded text-gray-700 font-bold text-lg"
          title="Zoom Out"
        >
          −
        </button>
        <div className="border-t border-gray-200" />
        <button
          onClick={fitToView}
          className="p-2 hover:bg-gray-100 rounded text-gray-700 text-xs font-medium"
          title="Fit to View"
        >
          Fit
        </button>
        <div className="border-t border-gray-200" />
        <button
          onClick={resetView}
          className="p-2 hover:bg-gray-100 rounded text-gray-700 text-xs font-medium"
          title="Reset View"
        >
          Reset
        </button>
      </div>

      {/* Help text */}
      <div className="absolute bottom-3 left-3 z-10 text-xs text-gray-500 bg-white/80 backdrop-blur px-2 py-1 rounded">
        Scroll to zoom • Drag nodes to move • Drag background to pan
      </div>

      {/* Scale indicator */}
      <div className="absolute bottom-3 right-3 z-10 text-xs text-gray-500 bg-white/80 backdrop-blur px-2 py-1 rounded">
        {Math.round(transformRef.current.scale * 100)}%
      </div>

      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: getCursor() }}
      />
    </div>
  );
};

// Relationships Tab - Network Graph Visualization
const RelationshipsTab: React.FC<{
  subdomains: Subdomain[];
  domainPackId: string;
}> = ({ subdomains, domainPackId }) => {
  const [allRelationships, setAllRelationships] = useState<ResearchRelationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubdomain, setSelectedSubdomain] = useState<string>('all');
  const [discoveringFor, setDiscoveringFor] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [focusedNode, setFocusedNode] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [hiddenRelTypes, setHiddenRelTypes] = useState<Set<string>>(new Set());
  const [hiddenNodeTypes, setHiddenNodeTypes] = useState<Set<string>>(new Set());
  const [showDetails, setShowDetails] = useState<boolean>(false);

  // Toggle relationship type visibility
  const toggleRelType = (relType: string) => {
    setHiddenRelTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(relType)) {
        newSet.delete(relType);
      } else {
        newSet.add(relType);
      }
      return newSet;
    });
  };

  // Toggle node type visibility
  const toggleNodeType = (nodeType: string) => {
    setHiddenNodeTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeType)) {
        newSet.delete(nodeType);
      } else {
        newSet.add(nodeType);
      }
      return newSet;
    });
  };

  // Show all types
  const showAllTypes = () => {
    setHiddenRelTypes(new Set());
    setHiddenNodeTypes(new Set());
  };

  // Fetch relationships for all subdomains
  React.useEffect(() => {
    const fetchAllRelationships = async () => {
      setLoading(true);
      try {
        const allRels: ResearchRelationship[] = [];
        for (const subdomain of subdomains) {
          try {
            const response = await getSubdomainRelationships(domainPackId, subdomain.subdomain_id);
            allRels.push(...response.relationships);
          } catch (err) {
            console.error(`Failed to fetch relationships for ${subdomain.name}:`, err);
          }
        }
        setAllRelationships(allRels);
      } catch (err) {
        console.error('Failed to fetch relationships:', err);
      } finally {
        setLoading(false);
      }
    };

    if (subdomains.length > 0) {
      fetchAllRelationships();
    } else {
      setLoading(false);
    }
  }, [subdomains, domainPackId]);

  // Handle discover relationships
  const handleDiscover = async (subdomainId: string) => {
    setDiscoveringFor(subdomainId);
    try {
      await triggerRelationshipDiscovery(domainPackId, subdomainId, true);
      // Refresh relationships after discovery
      setTimeout(async () => {
        const response = await getSubdomainRelationships(domainPackId, subdomainId);
        setAllRelationships(prev => {
          const filtered = prev.filter(r =>
            !response.relationships.some(nr => nr.relationshipId === r.relationshipId)
          );
          return [...filtered, ...response.relationships];
        });
        setDiscoveringFor(null);
      }, 30000); // Wait for LLM to process
    } catch (err) {
      console.error('Failed to trigger discovery:', err);
      setDiscoveringFor(null);
    }
  };

  // Build nodes from items
  const buildGraphData = () => {
    const nodes: { id: string; label: string; type: string; color: string; subdomain?: string }[] = [];
    const nodeIds = new Set<string>();
    const links: { source: string; target: string; type: string; strength: string; explanation: string }[] = [];

    // Color mapping for item types
    const typeColors: Record<string, string> = {
      Trend: '#3b82f6',       // blue
      Issue: '#ef4444',       // red
      BestPractice: '#10b981', // green
      DataElement: '#8b5cf6', // purple
      Hypothesis: '#f59e0b',  // amber
    };

    // Add nodes from relationships
    allRelationships.forEach(rel => {
      if (!nodeIds.has(rel.sourceId)) {
        nodeIds.add(rel.sourceId);
        nodes.push({
          id: rel.sourceId,
          label: rel.sourceTitle || rel.sourceId,
          type: rel.sourceType,
          color: typeColors[rel.sourceType] || '#6b7280',
        });
      }
      if (!nodeIds.has(rel.targetId)) {
        nodeIds.add(rel.targetId);
        nodes.push({
          id: rel.targetId,
          label: rel.targetTitle || rel.targetId,
          type: rel.targetType,
          color: typeColors[rel.targetType] || '#6b7280',
        });
      }
      links.push({
        source: rel.sourceId,
        target: rel.targetId,
        type: rel.relationshipType,
        strength: rel.strength,
        explanation: rel.explanation,
      });
    });

    return { nodes, links };
  };

  const graphData = buildGraphData();

  // Relationship type colors
  const relationshipTypeColors: Record<string, string> = {
    DRIVES: '#ef4444',
    ADDRESSED_BY: '#10b981',
    INVESTIGATES: '#f59e0b',
    REQUIRES: '#8b5cf6',
    VALIDATES: '#06b6d4',
    ACCELERATES: '#3b82f6',
    MITIGATES: '#22c55e',
    ENABLES: '#a855f7',
  };

  // Group relationships by type
  const relationshipsByType = allRelationships.reduce((acc, rel) => {
    if (!acc[rel.relationshipType]) {
      acc[rel.relationshipType] = [];
    }
    acc[rel.relationshipType].push(rel);
    return acc;
  }, {} as Record<string, ResearchRelationship[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2 text-neutral-600">Loading relationships...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900">Research Relationships</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Connections between trends, issues, practices, data elements, and hypotheses
            </p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={selectedSubdomain}
              onChange={(e) => setSelectedSubdomain(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            >
              <option value="all">All Subdomains</option>
              {subdomains.map((sub) => (
                <option key={sub.subdomain_id} value={sub.subdomain_id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-primary-50 rounded-lg">
            <div className="text-3xl font-bold text-primary-600">{allRelationships.length}</div>
            <div className="text-sm text-neutral-600 mt-1">Total Relationships</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">{graphData.nodes.length}</div>
            <div className="text-sm text-neutral-600 mt-1">Connected Items</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600">
              {Object.keys(relationshipsByType).length}
            </div>
            <div className="text-sm text-neutral-600 mt-1">Relationship Types</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-3xl font-bold text-purple-600">
              {allRelationships.filter(r => r.strength === 'high').length}
            </div>
            <div className="text-sm text-neutral-600 mt-1">High Strength</div>
          </div>
        </div>
      </div>

      {/* Discover relationships for subdomains without any */}
      {subdomains.some(sub => !allRelationships.some(r => r.sourceId.includes(sub.subdomain_id.slice(-8)))) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-2">
            Discover Relationships
          </h3>
          <p className="text-sm text-amber-700 mb-3">
            Some subdomains don't have relationships yet. Click to discover:
          </p>
          <div className="flex flex-wrap gap-2">
            {subdomains.map((sub) => (
              <button
                key={sub.subdomain_id}
                onClick={() => handleDiscover(sub.subdomain_id)}
                disabled={discoveringFor === sub.subdomain_id}
                className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 disabled:opacity-50 flex items-center gap-1"
              >
                {discoveringFor === sub.subdomain_id ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Discovering...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3 h-3" />
                    {sub.name}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Knowledge Graph Visualization */}
      {graphData.nodes.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900">Knowledge Graph</h3>
            <div className="flex items-center gap-4">
              {/* Filter by Type */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-600">Filter:</span>
                <select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setFocusedNode(null);
                  }}
                  className="px-2 py-1 text-sm border border-neutral-300 rounded-lg"
                >
                  <option value="all">All Types</option>
                  <option value="Issue">Issues Only</option>
                  <option value="Trend">Trends Only</option>
                  <option value="BestPractice">Best Practices Only</option>
                  <option value="DataElement">Data Elements Only</option>
                  <option value="Hypothesis">Hypotheses Only</option>
                </select>
              </div>
              {/* Focus on Specific Item */}
              {filterType !== 'all' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-600">Focus:</span>
                  <select
                    value={focusedNode || ''}
                    onChange={(e) => setFocusedNode(e.target.value || null)}
                    className="px-2 py-1 text-sm border border-neutral-300 rounded-lg max-w-[200px]"
                  >
                    <option value="">Show All {filterType}s</option>
                    {graphData.nodes
                      .filter(n => n.type === filterType)
                      .map(node => (
                        <option key={node.id} value={node.id}>
                          {node.label.length > 30 ? node.label.substring(0, 30) + '...' : node.label}
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}
              {focusedNode && (
                <button
                  onClick={() => setFocusedNode(null)}
                  className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200"
                >
                  Clear Focus
                </button>
              )}
              {/* Show Details Toggle */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className={`px-3 py-1 text-sm rounded-lg font-medium transition-colors ${
                  showDetails
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {showDetails ? '✓ Details On' : 'Show Details'}
              </button>
            </div>
          </div>

          {/* Enhanced focused view controls */}
          {focusedNode && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-amber-700 font-semibold">Focused:</span>
                  <span className="text-amber-900 font-medium">
                    {graphData.nodes.find(n => n.id === focusedNode)?.label}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded"
                    style={{
                      backgroundColor: graphData.nodes.find(n => n.id === focusedNode)?.color + '30',
                      color: graphData.nodes.find(n => n.id === focusedNode)?.color
                    }}
                  >
                    {graphData.nodes.find(n => n.id === focusedNode)?.type}
                  </span>
                </div>
                <button
                  onClick={() => { setFocusedNode(null); showAllTypes(); }}
                  className="px-3 py-1 text-sm bg-amber-200 text-amber-800 rounded-lg hover:bg-amber-300 font-medium"
                >
                  Exit Focus Mode
                </button>
              </div>

              {/* Filter controls for focused view */}
              <div className="grid grid-cols-2 gap-4">
                {/* Node Type Filters */}
                <div>
                  <div className="text-xs font-semibold text-amber-800 mb-2 uppercase">Show Node Types</div>
                  <div className="flex flex-wrap gap-2">
                    {['Trend', 'Issue', 'BestPractice', 'DataElement', 'Hypothesis'].map(type => {
                      const count = graphData.links
                        .filter(l => l.source === focusedNode || l.target === focusedNode)
                        .filter(l => {
                          const otherId = l.source === focusedNode ? l.target : l.source;
                          return graphData.nodes.find(n => n.id === otherId)?.type === type;
                        }).length;
                      if (count === 0) return null;
                      const isHidden = hiddenNodeTypes.has(type);
                      const colors: Record<string, string> = {
                        Trend: '#3b82f6', Issue: '#ef4444', BestPractice: '#10b981',
                        DataElement: '#8b5cf6', Hypothesis: '#f59e0b'
                      };
                      return (
                        <button
                          key={type}
                          onClick={() => toggleNodeType(type)}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                            isHidden
                              ? 'bg-neutral-100 text-neutral-400 line-through'
                              : 'bg-white border border-neutral-200 text-neutral-700 hover:border-neutral-400'
                          }`}
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: isHidden ? '#9ca3af' : colors[type] }}
                          />
                          {type === 'BestPractice' ? 'Practice' : type}
                          <span className="text-neutral-500">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Relationship Type Filters */}
                <div>
                  <div className="text-xs font-semibold text-amber-800 mb-2 uppercase">Show Relationship Types</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(relationshipTypeColors).map(([type, color]) => {
                      const count = graphData.links.filter(l =>
                        l.type === type && (l.source === focusedNode || l.target === focusedNode)
                      ).length;
                      if (count === 0) return null;
                      const isHidden = hiddenRelTypes.has(type);
                      return (
                        <button
                          key={type}
                          onClick={() => toggleRelType(type)}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                            isHidden
                              ? 'bg-neutral-100 text-neutral-400 line-through'
                              : 'bg-white border border-neutral-200 text-neutral-700 hover:border-neutral-400'
                          }`}
                        >
                          <div
                            className="w-4 h-0.5 rounded"
                            style={{ backgroundColor: isHidden ? '#9ca3af' : color }}
                          />
                          {type.replace(/_/g, ' ')}
                          <span className="text-neutral-500">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Show all button */}
              {(hiddenRelTypes.size > 0 || hiddenNodeTypes.size > 0) && (
                <button
                  onClick={showAllTypes}
                  className="mt-3 text-xs text-amber-700 hover:text-amber-900 underline"
                >
                  Show all hidden types
                </button>
              )}
            </div>
          )}

          <div className="relative border border-neutral-200 rounded-lg bg-gradient-to-br from-slate-50 to-neutral-100 overflow-hidden" style={{ height: '600px' }}>
            {/* Legend */}
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg z-10 border border-neutral-200">
              <div className="text-xs font-bold text-neutral-800 mb-3 uppercase tracking-wide">Node Types</div>
              <div className="space-y-2">
                <button
                  onClick={() => { setFilterType('Trend'); setFocusedNode(null); }}
                  className={`flex items-center gap-2 w-full text-left px-1 py-0.5 rounded ${filterType === 'Trend' ? 'bg-blue-50' : 'hover:bg-neutral-50'}`}
                >
                  <div className="w-4 h-4 rounded-full bg-blue-500 shadow-sm" />
                  <span className="text-sm text-neutral-700 font-medium">Trend</span>
                  <span className="text-xs text-neutral-500 ml-auto">({graphData.nodes.filter(n => n.type === 'Trend').length})</span>
                </button>
                <button
                  onClick={() => { setFilterType('Issue'); setFocusedNode(null); }}
                  className={`flex items-center gap-2 w-full text-left px-1 py-0.5 rounded ${filterType === 'Issue' ? 'bg-red-50' : 'hover:bg-neutral-50'}`}
                >
                  <div className="w-4 h-4 rounded-full bg-red-500 shadow-sm" />
                  <span className="text-sm text-neutral-700 font-medium">Issue</span>
                  <span className="text-xs text-neutral-500 ml-auto">({graphData.nodes.filter(n => n.type === 'Issue').length})</span>
                </button>
                <button
                  onClick={() => { setFilterType('BestPractice'); setFocusedNode(null); }}
                  className={`flex items-center gap-2 w-full text-left px-1 py-0.5 rounded ${filterType === 'BestPractice' ? 'bg-emerald-50' : 'hover:bg-neutral-50'}`}
                >
                  <div className="w-4 h-4 rounded-full bg-emerald-500 shadow-sm" />
                  <span className="text-sm text-neutral-700 font-medium">Best Practice</span>
                  <span className="text-xs text-neutral-500 ml-auto">({graphData.nodes.filter(n => n.type === 'BestPractice').length})</span>
                </button>
                <button
                  onClick={() => { setFilterType('DataElement'); setFocusedNode(null); }}
                  className={`flex items-center gap-2 w-full text-left px-1 py-0.5 rounded ${filterType === 'DataElement' ? 'bg-violet-50' : 'hover:bg-neutral-50'}`}
                >
                  <div className="w-4 h-4 rounded-full bg-violet-500 shadow-sm" />
                  <span className="text-sm text-neutral-700 font-medium">Data Element</span>
                  <span className="text-xs text-neutral-500 ml-auto">({graphData.nodes.filter(n => n.type === 'DataElement').length})</span>
                </button>
                <button
                  onClick={() => { setFilterType('Hypothesis'); setFocusedNode(null); }}
                  className={`flex items-center gap-2 w-full text-left px-1 py-0.5 rounded ${filterType === 'Hypothesis' ? 'bg-amber-50' : 'hover:bg-neutral-50'}`}
                >
                  <div className="w-4 h-4 rounded-full bg-amber-500 shadow-sm" />
                  <span className="text-sm text-neutral-700 font-medium">Hypothesis</span>
                  <span className="text-xs text-neutral-500 ml-auto">({graphData.nodes.filter(n => n.type === 'Hypothesis').length})</span>
                </button>
                {filterType !== 'all' && (
                  <button
                    onClick={() => { setFilterType('all'); setFocusedNode(null); }}
                    className="text-xs text-primary-600 hover:text-primary-800 mt-2"
                  >
                    Show All Types
                  </button>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-neutral-200">
                <div className="text-xs font-bold text-neutral-800 mb-2 uppercase tracking-wide">Relationships</div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0.5 bg-red-500" />
                    <span className="text-neutral-600">DRIVES</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0.5 bg-emerald-500" />
                    <span className="text-neutral-600">ADDRESSED_BY</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0.5 bg-amber-500" />
                    <span className="text-neutral-600">INVESTIGATES</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0.5 bg-blue-500" />
                    <span className="text-neutral-600">ACCELERATES</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Node Detail Panel */}
            {selectedNode && (
              <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg z-10 border border-neutral-200 max-w-sm">
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="text-xs font-bold px-2 py-1 rounded"
                    style={{
                      backgroundColor: graphData.nodes.find(n => n.id === selectedNode)?.color + '20',
                      color: graphData.nodes.find(n => n.id === selectedNode)?.color
                    }}
                  >
                    {graphData.nodes.find(n => n.id === selectedNode)?.type}
                  </span>
                  <button
                    onClick={() => setSelectedNode(null)}
                    className="text-neutral-400 hover:text-neutral-600"
                  >
                    ×
                  </button>
                </div>
                <h4 className="font-semibold text-neutral-900 mb-2">
                  {graphData.nodes.find(n => n.id === selectedNode)?.label}
                </h4>
                <div className="text-xs text-neutral-600 space-y-1">
                  <p><strong>Outgoing:</strong> {graphData.links.filter(l => l.source === selectedNode).length} relationships</p>
                  <p><strong>Incoming:</strong> {graphData.links.filter(l => l.target === selectedNode).length} relationships</p>
                </div>
                {graphData.links.filter(l => l.source === selectedNode || l.target === selectedNode).length > 0 && (
                  <div className="mt-3 pt-2 border-t border-neutral-200">
                    <p className="text-xs font-semibold text-neutral-700 mb-1">Connected via:</p>
                    <div className="flex flex-wrap gap-1">
                      {[...new Set(graphData.links
                        .filter(l => l.source === selectedNode || l.target === selectedNode)
                        .map(l => l.type)
                      )].map(type => (
                        <span
                          key={type}
                          className="text-xs px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: relationshipTypeColors[type] + '20',
                            color: relationshipTypeColors[type]
                          }}
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Focus button */}
                <div className="mt-3 pt-2 border-t border-neutral-200 flex gap-2">
                  <button
                    onClick={() => {
                      setFocusedNode(selectedNode);
                      setSelectedNode(null);
                    }}
                    className="flex-1 px-3 py-1.5 text-xs bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 font-medium"
                  >
                    Focus on this item
                  </button>
                </div>
              </div>
            )}

            {/* Force-directed Graph using Canvas */}
            <KnowledgeGraphCanvas
              nodes={graphData.nodes}
              links={graphData.links}
              selectedNode={selectedNode}
              onNodeClick={(nodeId) => setSelectedNode(nodeId === selectedNode ? null : nodeId)}
              relationshipColors={relationshipTypeColors}
              focusedNode={focusedNode}
              hiddenRelTypes={hiddenRelTypes}
              hiddenNodeTypes={hiddenNodeTypes}
              showDetails={showDetails}
            />
          </div>
        </div>
      )}

      {/* Relationships by Type */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Relationships by Type</h3>
        <div className="space-y-4">
          {Object.entries(relationshipsByType).map(([type, rels]) => (
            <div key={type} className="border border-neutral-200 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-neutral-50"
                style={{ borderLeft: `4px solid ${relationshipTypeColors[type] || '#6b7280'}` }}
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-neutral-900">{type.replace(/_/g, ' ')}</span>
                  <span className="text-sm text-neutral-500">({rels.length})</span>
                </div>
              </div>
              <div className="px-4 py-3 bg-neutral-50 space-y-2">
                {rels.map((rel) => (
                  <div
                    key={rel.relationshipId}
                    className="flex items-center gap-2 text-sm p-2 bg-white rounded border border-neutral-100"
                  >
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${relationshipTypeColors[type]}20`, color: relationshipTypeColors[type] }}
                    >
                      {rel.sourceType}
                    </span>
                    <span className="text-neutral-700 truncate max-w-[200px]" title={rel.sourceTitle}>
                      {rel.sourceTitle}
                    </span>
                    <ArrowRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${relationshipTypeColors[type]}20`, color: relationshipTypeColors[type] }}
                    >
                      {rel.targetType}
                    </span>
                    <span className="text-neutral-700 truncate max-w-[200px]" title={rel.targetTitle}>
                      {rel.targetTitle}
                    </span>
                    <span
                      className={`ml-auto px-2 py-0.5 rounded text-xs ${
                        rel.strength === 'high'
                          ? 'bg-green-100 text-green-700'
                          : rel.strength === 'medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-neutral-100 text-neutral-600'
                      }`}
                    >
                      {rel.strength}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {allRelationships.length === 0 && (
          <div className="text-center py-8 text-neutral-500">
            <Network className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
            <p>No relationships discovered yet.</p>
            <p className="text-sm mt-1">
              Click "Discover Relationships" on a subdomain to analyze connections.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainPackDetail;
